import { WGSL_LAYOUT, BLOB2_OFFSETS } from "../../../compute/compute_layout_constants";
import {printAliveBlobs,printAliveBlobsCircleTypes} from "../../../compute/blob_helpers";

import {printUsedStickers, code_stats} from "../../../compute/stickergpu_helpers";

const BLOB_IN_STICKER_FAIL_NN0 = 1n;
const BLOB_IN_STICKER_FAIL_NN1 = 2n;
const BLOB_IN_STICKER_FAIL_NN2 = 3n;
const BLOB_IN_STICKER_FAIL_GET_CIRCLES = 4n;
const BLOB_IN_STICKER_FAIL_VALIDATE_CIRCLES = 5n;
const BLOB_IN_STICKER_FAIL_VALIDATE_CORNERS = 6n;
const BLOB_IN_STICKER_FAIL_ORIENTATION_CONFIDENCE = 7n;

const ERROR_VALIDATE_CIRCLES = 10n;
const DEBUG_VALIDATE_CIRCLES = 11n;
const ERROR_VALIDATE_CENTRAL_CIRCLE = 12n;
const DEBUG_VALIDATE_CENTRAL_CIRCLE = 13n;
const ERROR_VALIDATE_CORNERS = 14n;
const DEBUG_VALIDATE_CORNERS = 15n;
const DEBUG_PASSED_STAGE = 16n;

const BLOB2_STRIDE_BYTES = WGSL_LAYOUT.Blob2.size;
const BLOB2_STRIDE_WORDS = WGSL_LAYOUT.Blob2.strideWords;
const STICKERINFO_STRIDE_WORDS = WGSL_LAYOUT.StickerInfoGpu.strideWords;

export default class StickerDebug {
  constructor() {}

  log_error(code: bigint, debug_stage: number, index: number) {
    const s = this.get_code_str(code);
    console.log("[sticker error] " + index + " - " + s + " stage: " + debug_stage);
  }

  log_debug(code: bigint, debug_stage: number, index: number) {
    const s = this.get_code_str(code);
    console.log("[sticker debug] " + index + " - " + s + " stage: " + debug_stage);
  }

  log_sticker(words: Uint32Array, index: number) {
    console.log("[log_sticker] not implemented yet, index =", index, "totalWords =", words.length);
  }

  dumpFirst4StickerRecords(words: Uint32Array) {
    const stride = STICKERINFO_STRIDE_WORDS;
    const count = 4;
    const total = stride * count;

    if (words.length < total) {
      console.log(
        "[sticker dump] words too short:",
        words.length,
        "expected at least:",
        total
      );
      return;
    }

    const firstWords = words.slice(0, total);

    console.log(`[sticker dump] first ${total} u32:`);
    console.log(Array.from(firstWords));

    for (let i = 0; i < count; i++) {
      const base = i * stride;
      const record = firstWords.slice(base, base + stride);
      console.log(`[sticker dump] record ${i} base ${base}:`, Array.from(record));
    }
  }

  get_code_str(code: bigint) {
    switch (code) {
      case BLOB_IN_STICKER_FAIL_NN0: return "BLOB_IN_STICKER_FAIL_NN0";
      case BLOB_IN_STICKER_FAIL_NN1: return "BLOB_IN_STICKER_FAIL_NN1";
      case BLOB_IN_STICKER_FAIL_NN2: return "BLOB_IN_STICKER_FAIL_NN2";
      case BLOB_IN_STICKER_FAIL_GET_CIRCLES: return "BLOB_IN_STICKER_FAIL_GET_CIRCLES";
      case BLOB_IN_STICKER_FAIL_VALIDATE_CIRCLES: return "BLOB_IN_STICKER_FAIL_VALIDATE_CIRCLES";
      case BLOB_IN_STICKER_FAIL_VALIDATE_CORNERS: return "BLOB_IN_STICKER_FAIL_VALIDATE_CORNERS";
      case BLOB_IN_STICKER_FAIL_ORIENTATION_CONFIDENCE: return "BLOB_IN_STICKER_FAIL_ORIENTATION_CONFIDENCE";

      case ERROR_VALIDATE_CIRCLES: return "ERROR_VALIDATE_CIRCLES";
      case DEBUG_VALIDATE_CIRCLES: return "DEBUG_VALIDATE_CIRCLES";
      case ERROR_VALIDATE_CENTRAL_CIRCLE: return "ERROR_VALIDATE_CENTRAL_CIRCLE";
      case DEBUG_VALIDATE_CENTRAL_CIRCLE: return "DEBUG_VALIDATE_CENTRAL_CIRCLE";
      case ERROR_VALIDATE_CORNERS: return "ERROR_VALIDATE_CORNERS";
      case DEBUG_VALIDATE_CORNERS: return "DEBUG_VALIDATE_CORNERS";
      case DEBUG_PASSED_STAGE: return "DEBUG_PASSED_STAGE";
    }

    return "code: " + code;
  }

  private makeBlob2Readers(words: Uint32Array, index: number) {
    const baseWord = index * BLOB2_STRIDE_WORDS;
    const baseByte = baseWord << 2;

    if (baseWord + BLOB2_STRIDE_WORDS > words.length) {
      return {
        ok: false as const,
        error: {
          index,
          error: "out_of_range",
          baseWord,
          strideWords: BLOB2_STRIDE_WORDS,
          wordsLength: words.length,
        },
      };
    }

    const view = new DataView(words.buffer, words.byteOffset, words.byteLength);

    const i32 = (byteOff: number) => view.getInt32(baseByte + byteOff, true);
    const u32 = (byteOff: number) => view.getUint32(baseByte + byteOff, true);
    const f32 = (byteOff: number) => view.getFloat32(baseByte + byteOff, true);

    return {
      ok: true as const,
      baseWord,
      baseByte,
      i32,
      u32,
      f32,
    };
  }

  private readVCbCrPos(f32: (n: number) => number, i32: (n: number) => number, byteOff: number) {
    return {
      v: f32(byteOff + 0),
      cb: f32(byteOff + 4),
      cr: f32(byteOff + 8),
      x: i32(byteOff + 12),
      y: i32(byteOff + 16),
      xf: f32(byteOff + 20),
      yf: f32(byteOff + 24),
      pad0: i32(byteOff + 28),
    };
  }

  private readSpinDebug(f32: (n: number) => number, i32: (n: number) => number, byteOff: number) {
    return {
      normalized_up: [f32(byteOff + 0), f32(byteOff + 4)],
      split_dir: [f32(byteOff + 8), f32(byteOff + 12)],
      ang_degrees: f32(byteOff + 16),
      ang_wrapped: f32(byteOff + 20),
      spin: i32(byteOff + 24),
      circle: i32(byteOff + 28),
    };
  }

  private readEvidence(
    f32: (n: number) => number,
    i32: (n: number) => number,
    u32: (n: number) => number,
    byteOff: number
  ) {
    return {
      blueScore: f32(byteOff + 0),
      greenScore: f32(byteOff + 4),
      redScore: f32(byteOff + 8),
      blackScore: f32(byteOff + 12),
      unknownScore: f32(byteOff + 16),
      totalWeight: f32(byteOff + 20),
      colorWeight: f32(byteOff + 24),
      blueGreenScore: f32(byteOff + 28),
      redVsBlueGreen: f32(byteOff + 32),
      blueFracColor: f32(byteOff + 36),
      greenFracColor: f32(byteOff + 40),
      redFracColor: f32(byteOff + 44),
      blueHits: i32(byteOff + 48),
      greenHits: i32(byteOff + 52),
      redHits: i32(byteOff + 56),
      blackHits: i32(byteOff + 60),
      unknownHits: i32(byteOff + 64),
      sampleCount: i32(byteOff + 68),
      hasBlue: u32(byteOff + 72),
      hasGreen: u32(byteOff + 76),
      hasRed: u32(byteOff + 80),
      hasBlack: u32(byteOff + 84),
    };
  }

  readBlob2(words: Uint32Array, index: number) {
    const r = this.makeBlob2Readers(words, index);
    if (!r.ok) return r.error;

    const { i32, u32, f32 } = r;

    return {
      index,
      strideBytes: BLOB2_STRIDE_BYTES,
      strideWords: BLOB2_STRIDE_WORDS,

      alive: i32(BLOB2_OFFSETS.alive),
      x: i32(BLOB2_OFFSETS.x),
      y: i32(BLOB2_OFFSETS.y),
      width: i32(BLOB2_OFFSETS.width),
      height: i32(BLOB2_OFFSETS.height),

      nn_0: i32(BLOB2_OFFSETS.nn_0),
      nn_dist_1: f32(BLOB2_OFFSETS.nn_dist_1),
      nn_1: i32(BLOB2_OFFSETS.nn_1),
      nn_dist_0: f32(BLOB2_OFFSETS.nn_dist_0),
      nn_2: i32(BLOB2_OFFSETS.nn_2),
      nn_dist_2: f32(BLOB2_OFFSETS.nn_dist_2),
      sticker_count: i32(BLOB2_OFFSETS.sticker_count),
      pad0: i32(BLOB2_OFFSETS.pad0),

      color_pair: {
        vcb0: this.readVCbCrPos(f32, i32, BLOB2_OFFSETS.color_pair + 0),
        vcb1: this.readVCbCrPos(f32, i32, BLOB2_OFFSETS.color_pair + 32),
      },

      ndir: [
        f32(BLOB2_OFFSETS.ndir + 0),
        f32(BLOB2_OFFSETS.ndir + 4),
      ],

      orientation_confidence: f32(BLOB2_OFFSETS.orientation_confidence),
      circle_type: i32(BLOB2_OFFSETS.circle_type),
      color_index: i32(BLOB2_OFFSETS.color_index),
      blue_perc: f32(BLOB2_OFFSETS.blue_perc),
      green_perc: f32(BLOB2_OFFSETS.green_perc),
      red_perc: f32(BLOB2_OFFSETS.red_perc),
      black_perc: f32(BLOB2_OFFSETS.black_perc),
      fail_code: i32(BLOB2_OFFSETS.fail_code),
      black_marker: i32(BLOB2_OFFSETS.black_marker),
      pad2: i32(BLOB2_OFFSETS.pad2),

      spin_debug: this.readSpinDebug(f32, i32, BLOB2_OFFSETS.spin_debug),
      evidence: this.readEvidence(f32, i32, u32, BLOB2_OFFSETS.evidence),
    };
  }

  dumpBlob2(words: Uint32Array, index: number): void {
    const blob = this.readBlob2(words, index);
    console.log("[blob dump]", JSON.stringify(blob, null, 2));
  }

  dumpAllBlob2Json(words: Uint32Array): void {
    const maxBlobs = Math.floor(words.length / BLOB2_STRIDE_WORDS);
    const records = [];

    for (let i = 0; i < maxBlobs; i++) {
      records.push(this.readBlob2(words, i));
    }

    const payload = {
      kind: "blob2_dump",
      maxBlobs,
      strideBytes: BLOB2_STRIDE_BYTES,
      strideWords: BLOB2_STRIDE_WORDS,
      totalWords: words.length,
      records,
    };

    let aliveCount = 0;
    for (let i = 0; i < maxBlobs; i++) {
      const blob = this.readBlob2(words, i);
      if (!("error" in blob) && blob.alive === 1) aliveCount++;
    }

    console.log("aliveCount in mapped blob dump =", aliveCount);
    console.log(JSON.stringify(payload, null, 2));
  }

  dumpAliveValues(words: Uint32Array, count = 16): void {
    const blobCount = Math.floor(words.length / BLOB2_STRIDE_WORDS);
    const n = Math.min(count, blobCount);
    const rows: Array<{ blobIndex: number; aliveWordOffset: number; alive: number }> = [];

    for (let blobIndex = 0; blobIndex < n; blobIndex++) {
      const aliveWordOffset = blobIndex * BLOB2_STRIDE_WORDS;
      rows.push({
        blobIndex,
        aliveWordOffset,
        alive: words[aliveWordOffset],
      });
    }

    console.log("[dumpAliveValues]", JSON.stringify(rows, null, 2));
  }

  scanBlobWordsForOne(words: Uint32Array): void {
    const hits: Array<{ wordOffset: number; byteOffset: number }> = [];

    for (let i = 0; i < words.length; i++) {
      if (words[i] === 1) {
        hits.push({
          wordOffset: i,
          byteOffset: i * 4,
        });
      }
    }

    console.log("[scanBlobWordsForOne] hitCount =", hits.length);
    console.log(JSON.stringify(hits, null, 2));
  }

  scanAlive(words: Uint32Array): void {
    const blobCount = Math.floor(words.length / BLOB2_STRIDE_WORDS);
    const aliveIndices: number[] = [];

    for (let blobIndex = 0; blobIndex < blobCount; blobIndex++) {
      const aliveWord = blobIndex * BLOB2_STRIDE_WORDS;
      if (words[aliveWord] === 1) {
        aliveIndices.push(blobIndex);
      }
    }

    console.log("aliveIndices:", aliveIndices);
    console.log("aliveCount:", aliveIndices.length);
  }

  scanAliveMatching(words: Uint32Array, predicate: (alive: number, blobIndex: number) => boolean): number[] {
    const blobCount = Math.floor(words.length / BLOB2_STRIDE_WORDS);
    const hits: number[] = [];

    for (let blobIndex = 0; blobIndex < blobCount; blobIndex++) {
      const alive = words[blobIndex * BLOB2_STRIDE_WORDS];
      if (predicate(alive, blobIndex)) {
        hits.push(blobIndex);
      }
    }

    console.log("matchingAliveBlobIndices =", hits);
    console.log("matchingAliveCount =", hits.length);
    return hits;
  }

  scanAliveRange(words: Uint32Array, minInclusive: number, maxExclusive: number): number[] {
    return this.scanAliveMatching(
      words,
      (alive) => alive >= minInclusive && alive < maxExclusive
    );
  }

  explainHit(wordOffset: number): { blobIndex: number; wordInBlob: number } {
    const blobIndex = Math.floor(wordOffset / BLOB2_STRIDE_WORDS);
    const wordInBlob = wordOffset - blobIndex * BLOB2_STRIDE_WORDS;
    return { blobIndex, wordInBlob };
  }

  analyzeAliveOffsets(offsets: number[]): void {
    offsets = offsets.slice().sort((a, b) => a - b);

    const diffs: number[] = [];
    for (let i = 1; i < offsets.length; i++) {
      diffs.push(offsets[i] - offsets[i - 1]);
    }

    console.log("alive word offsets:", offsets);
    console.log("diffs:", diffs);
  }

  scanBlobAliveDetailed(words: Uint32Array): void {
    const blobCount = Math.floor(words.length / BLOB2_STRIDE_WORDS);
    const hits: Array<{
      blobIndex: number;
      wordOffset: number;
      byteOffset: number;
      alive: number;
    }> = [];

    for (let blobIndex = 0; blobIndex < blobCount; blobIndex++) {
      const wordOffset = blobIndex * BLOB2_STRIDE_WORDS;
      if (words[wordOffset] === 1) {
        hits.push({
          blobIndex,
          wordOffset,
          byteOffset: wordOffset * 4,
          alive: words[wordOffset],
        });
      }
    }

    console.log(JSON.stringify(hits, null, 2));
  }

  scanBlobAlive(words: Uint32Array): number[] {
    const blobCount = Math.floor(words.length / BLOB2_STRIDE_WORDS);
    const aliveBlobIndices: number[] = [];

    for (let blobIndex = 0; blobIndex < blobCount; blobIndex++) {
      const aliveWordOffset = blobIndex * BLOB2_STRIDE_WORDS;
      if (words[aliveWordOffset] === 1) {
        aliveBlobIndices.push(blobIndex);
      }
    }

    console.log("aliveBlobIndices =", aliveBlobIndices);
    console.log("aliveCount =", aliveBlobIndices.length);
    return aliveBlobIndices;
  }

  print_blobs(words: Uint32Array):void
  {
      printAliveBlobsCircleTypes(words ,256);

  }

  print_stickers(stickerwords:Uint32Array,blob_words:Uint32Array)
  {
      printUsedStickers(stickerwords,blob_words,256);

  }

  bad_code(stickerwords:Uint32Array,index:number,blobwords:Uint32Array)
  {
      code_stats(stickerwords,index,blobwords);
  }
}
